import * as vp from 'vopay';
import { Box, SubTitle, Table } from 'vopay';
import { FunctionComponent, useContext, useEffect, useState } from 'react';

import { BillingDetailsContext } from '@/providers/BillingDetails.provider';
import { useTranslation } from 'react-i18next';

const PlanSettingsList: FunctionComponent<Record<never, never>> = () => {
  const details = useContext(BillingDetailsContext);

  const { t } = useTranslation('translations', { keyPrefix: 'BillingPackage.Details.PlanSettings' });

  const [planSettings, setPlanSettings] = useState<vp.PlanSettings>();
  const [planType, setPlanType] = useState<'pay as you go' | 'subscription'>();
  const [currency, setCurrency] = useState<'CAD' | 'USD'>();

  useEffect(() => {
    setPlanSettings(details?.PlanSettings);
    setPlanType(details?.BillingSettings?.PlanType.value);
    setCurrency(details?.BillingSettings?.Currency.value);
  }, [details]);

  return (
    <div>
      {Object.entries(planSettings || {})
        .filter(([, { permissions }]) => !isDisabled(permissions))
        .filter(([service]) => isAvailableForCurrency(service))
        .map(([service, { payAsYouGo, subscription, common }]) => {
          const planTypes = { 'pay as you go': payAsYouGo, subscription };
          const fees = planType ? { ...planTypes[planType], ...common } : common;

          const tableProps: vp.TableProps<vp.PlanFee<string>> = {
            id: 'plan-settings-list',
            version: '2',
            zebra: true,
            columns: [
              {
                id: 'feeLabel',
                header: t('tableHeader'),
                align: 'left',
                template: ({ data }) => data.label,
              },
              {
                id: 'feeValue',
                header: '',
                align: 'left',
                template: ({ data }) => {
                  const v = data.value || '';

                  const typeHandlers: Record<string, () => React.ReactNode> = {
                    percent: () => (v ? `${v}%` : '0.00%'),
                    amount: () => (v ? `$${v}` : '$0.00'),
                    boolean: () => v.toString(),
                    select: () => v.toString(),
                  };

                  return typeHandlers[data.type]();
                },
              },
            ],
            rows: Object.entries(fees).map(([key, { label, value, type }]) => ({
              id: key,
              data: { label, value, type },
            })),
          };

          return (
            <div key={`plan-settings-${service}`}>
              <Box mb="md">
                <SubTitle subTitle={service} />
              </Box>
              <Table {...tableProps} />
            </div>
          );
        })}
    </div>
  );

  function isDisabled(permissions: { [key: string]: { value: boolean | null } }) {
    const valueObjects = Object.values(permissions);

    return valueObjects.length === 0 ? false : !valueObjects.some(({ value }) => value);
  }

  function isAvailableForCurrency(service: string) {
    const restricted = {
      USD: ['Interac Money Request', 'Interac Bulk Payout', 'Inbound eTransfer', 'Vopay Instant Withdraw', 'OCT'],
      CAD: [],
    };

    const restrictedServices = currency ? restricted[currency] : [];

    return !restrictedServices.includes(service);
  }
};

export default PlanSettingsList;
